/**
 * Claro API client.
 *
 * Thin wrapper around fetch with session cookie auth, error handling,
 * 401 detection, and transparent token refresh.
 */

import { nudgeReconnect } from '@/stores/websocket'
import { apiUrl } from '@/lib/api-url'
import { noteServedRealm } from '@/lib/served-realm'
import { isFromClaro } from './claro-refusal'

const BASE = '/api/v1'

export class ApiError extends Error {
  status: number
  detail: string
  body?: unknown
  /** Server-suggested wait from a `Retry-After` header (429s carry one), in ms. */
  retryAfterMs?: number
  constructor(status: number, detail: string, body?: unknown) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.body = body
  }
}

/**
 * What a session refresh established (CLA-1529).
 *
 * - `refreshed`: new session cookies are set.
 * - `rejected`: the server refused the credential, so the session is over.
 * - `unavailable`: nobody could decide. The server errored (5xx), rate-limited
 *   us (429), or the network failed. The refresh cookie may be perfectly good.
 * - `not_from_claro`: a 400/401/403 came back, but not from Claro: a
 *   Cloudflare block or challenge page, Cloudflare's JSON error, anything that
 *   is not Claro's JSON (`claro-refusal.ts`, CLA-1541). Nothing refused the
 *   credential, so the session is kept. Not retried with backoff: an edge
 *   block does not lift in seconds, and waiting only delays the message.
 *
 * Only `rejected` may end a session. Treating `unavailable` as a rejection
 * told a clinician "your session has expired" (and, at boot, sent them to
 * /login) whenever a refresh coincided with a deploy or a dropped connection.
 * Same rule as Android (#136) and iOS (`AppState.doRefreshTokens`).
 */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable' | 'not_from_claro'

/**
 * Statuses that mean "this credential is refused" WHEN CLARO SAYS THEM.
 * `/auth/session-refresh` answers 401 for a missing, expired, revoked or
 * unknown token and for a deactivated account. 400 and 403 are included
 * defensively: neither is a transient failure, so waiting one out would never
 * end. The status alone is not enough: see `isFromClaro` (CLA-1541).
 */
export function isRefreshRejection(status: number): boolean {
  return status === 400 || status === 401 || status === 403
}

/**
 * Backoff between refresh attempts while the outcome is `unavailable`.
 * Mutable only so tests can run it without real waits.
 */
export const refreshRetry = { delaysMs: [1000, 2000, 4000] }

async function attemptRefresh(): Promise<RefreshOutcome> {
  try {
    const res = await fetch(apiUrl('/auth/session-refresh'), {
      method: 'POST',
      credentials: 'include',
    })
    if (res.ok) {
      // Fresh session cookies — if the WebSocket is down (its cookie-based
      // auth may be exactly why), retry it now instead of waiting out the
      // reconnect backoff. No-op when the socket is healthy.
      nudgeReconnect()
      return 'refreshed'
    }
    if (!isRefreshRejection(res.status)) return 'unavailable'
    // CLA-1541: only Claro's own refusal ends the session. An edge or WAF 403
    // page with the same status is not an answer about the credential.
    return (await isFromClaro(res)) ? 'rejected' : 'not_from_claro'
  } catch {
    return 'unavailable'
  }
}

async function refreshWithBackoff(): Promise<RefreshOutcome> {
  let outcome = await attemptRefresh()
  for (const delay of refreshRetry.delaysMs) {
    if (outcome !== 'unavailable') break
    await new Promise((r) => setTimeout(r, delay))
    outcome = await attemptRefresh()
  }
  return outcome
}

// Prevent concurrent refresh attempts — one at a time, backoff included, so a
// burst of 401s shares one refresh rather than rotating the token N times.
let refreshPromise: Promise<RefreshOutcome> | null = null

function tryRefreshSession(): Promise<RefreshOutcome> {
  if (refreshPromise) return refreshPromise
  refreshPromise = refreshWithBackoff().finally(() => { refreshPromise = null })
  return refreshPromise
}

/** The error a request fails with when its refresh could not be decided. */
export const REFRESH_UNAVAILABLE_DETAIL =
  "Can't reach Claro right now. You're still signed in, so try again in a moment."

/**
 * The error a request fails with when something in front of Claro answered
 * the refresh (CLA-1541). The same words as the apps' `edgeBlockedMessage` /
 * `EDGE_BLOCKED_MESSAGE` (CLA-1390): "check your connection" cannot help when
 * the edge refused it, and the support address does not go through that edge.
 */
export const REFRESH_NOT_FROM_CLARO_DETAIL =
  "We couldn't reach Claro. If this keeps happening, email hello@claro.clinic"

const MAX_502_RETRIES = 3
const RETRY_BASE_MS = 1000

// --- Client install header (adoption/version visibility, CLA-801) ---
// Sent as `X-Claro-Client` on every request so the backend can record which
// build each user runs, per device. Grammar (claro-elixir CLA-798):
//   <platform>; <app_version>; <os_version>; <install_id>; <device_model>

function installId(): string {
  const KEY = 'claro_install_id'
  try {
    let id = localStorage.getItem(KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    // Private mode / storage disabled — still report, just non-durable.
    return 'web-ephemeral'
  }
}

function coarseOS(): string {
  const ua = navigator.userAgent
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Android/.test(ua)) return 'Android'
  if (/Linux/.test(ua)) return 'Linux'
  return ''
}

function coarseBrowser(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'Edge'
  if (/OPR\/|Opera/.test(ua)) return 'Opera'
  if (/Chrome\//.test(ua)) return 'Chrome'
  if (/Firefox\//.test(ua)) return 'Firefox'
  if (/Safari\//.test(ua)) return 'Safari'
  return ''
}

let clientHeaderCache: string | null = null
function clientHeader(): string {
  if (clientHeaderCache) return clientHeaderCache
  const version = import.meta.env.VITE_RELEASE || 'dev'
  clientHeaderCache = `web; ${version}; ${coarseOS()}; ${installId()}; ${coarseBrowser()}`
  return clientHeaderCache
}

/**
 * Gateway statuses Cloudflare returns while an origin is briefly unreachable:
 * a deploy rolling a task, or a tunnel connector joining or leaving. Measured
 * on AU prod 2026-09-25: a two-task roll surfaced one edge 503 at the moment a
 * new connector registered, and only 502 was retried here, so it reached the
 * clinician (a failed recording start, if it hit the draft create).
 *
 Each is retried only when the body is NOT Claro's JSON. The edge's own
 * gateway pages (Cloudflare, the ALB) are HTML. Claro's own 502s and 503s
 * mean something: a 503 (`passkeys_unavailable`, `pdf_unavailable`, ...) only
 * delays the message if retried, and a 502 marks a downstream failure on an
 * action with side effects (EMR push, Stripe, an email or text send).
 * Retrying one of those repeats the side effect. Until 2026-10-08 every 502
 * was retried whatever the body, so a JSON 502 from "Push to EMR" or a
 * patient handoff send ran up to four times. Every consult write this retries is safe to
 * repeat: `audio_append` is keyed on `chunk_index`, and the draft create and
 * the generate commit carry an Idempotency-Key.
 */
const EDGE_GATEWAY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 530])

export function isEdgeGatewayError(res: Pick<Response, 'status' | 'headers'>): boolean {
  if (!EDGE_GATEWAY_STATUSES.has(res.status)) return false
  return !(res.headers.get('content-type') ?? '').includes('application/json')
}

async function request<T>(
  path: string,
  init?: RequestInit,
  _retryCount = 0,
  // Most calls use the /api/v1 base; pass '/api' for the telehealth meeting
  // routes registered under `scope "/api"` (admit/decline) which have no /v1.
  base: string = BASE,
  // 'response' hands back the unread Response on success — for binary bodies
  // (a PDF) whose caller needs the blob and the headers. Errors are still
  // parsed into an ApiError exactly as for JSON.
  as: 'json' | 'response' = 'json',
): Promise<T> {
  const res = await fetch(apiUrl(`${base}${path}`), {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      'X-Claro-Client': clientHeader(),
      ...init?.headers,
    },
  })
  // The consent page and the signed-out auth pages have no session realm:
  // the deployment that answered names it (CLA-1829). In the app it is the
  // signed-in realm's own deployment, so nothing changes there.
  noteServedRealm(res)

  // Retry the edge's gateway errors (a deploy rolling a task, a tunnel
  // connector joining or leaving) with exponential backoff.
  if (isEdgeGatewayError(res) && _retryCount < MAX_502_RETRIES) {
    await new Promise((r) => setTimeout(r, RETRY_BASE_MS * 2 ** _retryCount))
    return request<T>(path, init, _retryCount + 1, base, as)
  }

  if (res.status === 401) {
    // Try silent refresh before giving up
    const outcome = await tryRefreshSession()
    if (outcome === 'unavailable' || outcome === 'not_from_claro') {
      // Not a sign-out (CLA-1529, CLA-1541): no auth-error event, and not a
      // 401, which every caller reads as "signed out" (`useAuthStore.init`
      // redirects to /login on it). A 503 takes the transient paths instead:
      // init keeps the session and retries, and the next call tries the
      // refresh again.
      throw new ApiError(
        503,
        outcome === 'unavailable' ? REFRESH_UNAVAILABLE_DETAIL : REFRESH_NOT_FROM_CLARO_DETAIL,
      )
    }
    if (outcome === 'refreshed') {
      // Retry the original request with fresh cookies
      const retry = await fetch(apiUrl(`${base}${path}`), {
        credentials: 'include',
        ...init,
        headers: {
          Accept: 'application/json',
          'X-Claro-Client': clientHeader(),
          ...init?.headers,
        },
      })
      if (retry.ok) {
        if (as === 'response') return retry as T
        if (retry.status === 204) return undefined as T
        return retry.json()
      }
      // Retry also failed with non-ok — check if it's still auth
      if (retry.status === 401) {
        window.dispatchEvent(new CustomEvent('claro:auth-error'))
        throw new ApiError(401, 'Not authenticated')
      }
      let detail = `Error ${retry.status}`
      try {
        const body = await retry.json()
        detail = body.detail || detail
      } catch { /* ignore */ }
      throw new ApiError(retry.status, detail)
    }
    // Refresh refused — the session really is over
    window.dispatchEvent(new CustomEvent('claro:auth-error'))
    throw new ApiError(401, 'Not authenticated')
  }

  if (!res.ok) {
    let detail = `Error ${res.status}`
    let body: unknown
    try {
      body = await res.json()
      // `detail` is the house convention, but a good part of the admin API
      // (the whole feedback console, for one) answers with `error` instead —
      // so every one of those messages was being dropped and shown to the
      // operator as a bare "Error 409", losing the sentence that says what to
      // do next. Fall back rather than rewrite dozens of endpoints.
      const b = body as { detail?: string; error?: string }
      detail = b.detail || b.error || detail
    } catch {
      // ignore
    }
    // Plan gate (CLA-906): one global event, mirroring `claro:auth-error`,
    // so every gated surface gets the upgrade panel without per-call wiring.
    // note_cap_reached deliberately NOT included — the recorder owns that
    // moment inline (a modal over a live clinical flow is the wrong shape).
    // `write_locked` (CLA-1167) rides the same event. It arrives from a
    // pipeline plug rather than a feature gate, so it carries no `feature` —
    // the panel keys off the code instead and explains the state rather than
    // pitching one capability. Routing it here means every write surface in
    // the app gets the explanation without per-call wiring, which matters
    // because a locked clinic can hit it from almost anywhere.
    const code = (body as { code?: string } | undefined)?.code
    if (res.status === 402 && (code === 'plan_required' || code === 'write_locked')) {
      window.dispatchEvent(new CustomEvent('claro:plan-required', { detail: body }))
    }
    // Email-verification hard gate (CLA-1018). The shell's gate keys off
    // `/auth/me`, which a session that crosses the 7-day line while open never
    // re-reads; the first 403 from anywhere re-reads it instead.
    if (res.status === 403 && code === 'email_unverified') {
      window.dispatchEvent(new CustomEvent('claro:email-unverified', { detail: body }))
    }
    const err = new ApiError(res.status, detail, body)
    // Surface Retry-After (seconds form) so callers can honour it — the auth
    // store uses it to schedule its /auth/me retry after a 429 (CLA-1015).
    const retryAfter = Number(res.headers.get('Retry-After'))
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      err.retryAfterMs = retryAfter * 1000
    }
    throw err
  }

  if (as === 'response') return res as T
  // Handle 204 No Content
  if (res.status === 204) return undefined as T
  return res.json()
}

// --- Convenience methods ---

export const api = {
  get<T>(path: string, opts?: { headers?: Record<string, string> }): Promise<T> {
    return request<T>(path, opts?.headers ? { headers: opts.headers } : undefined)
  },

  /**
   * GET a binary body (a PDF). Same auth, 401-refresh and error handling as
   * `get`, so a refusal still arrives as an ApiError carrying the server's
   * sentence; on success the Response comes back unread.
   */
  getResponse(path: string): Promise<Response> {
    return request<Response>(path, undefined, 0, BASE, 'response')
  },

  /**
   * `opts.signal` cancels a request the caller no longer wants (a search superseded by typing).
   * `opts.headers` adds request headers (e.g. `x-clinic-id` for a clinic-scoped write).
   */
  post<T>(path: string, body?: unknown, opts?: { signal?: AbortSignal; headers?: Record<string, string> }): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...opts?.headers,
      },
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
      ...(opts?.signal ? { signal: opts.signal } : {}),
    })
  },

  /**
   * POST to the bare `/api` base (no `/v1`) — for the telehealth meeting routes
   * registered under `scope "/api"` (admit/decline). Same auth + 401-refresh +
   * error handling as `post`.
   */
  postApi<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(
      path,
      {
        method: 'POST',
        headers: body instanceof FormData ? {} : { 'Content-Type': 'application/json' },
        body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
      },
      0,
      '/api',
    )
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    })
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' })
  },

  /** POST with FormData (for file uploads, form-encoded data) */
  postForm<T>(path: string, data: FormData, opts?: { headers?: Record<string, string> }): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      headers: opts?.headers ? { ...opts.headers } : undefined,
      body: data,
    })
  },

  /** PUT with FormData */
  putForm<T>(path: string, data: FormData): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      body: data,
    })
  },
}
